import React, { useState } from "react";
import { Card, CardBody, CardTitle, Badge, Spinner } from "reactstrap";
import { useQuery } from "react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import leadEnquiriesQueries from "../../queries/leadEnquiriesQueries";
import { PaginationComponent } from "../../components/Component";
import { format } from "date-fns";

/**
 * Component to display lead enquiries
 * @param {Object} props - Component props
 * @param {Object} props.user - User object
 * @returns {JSX.Element} - Rendered component
 */
const LeadEnquiriesContainer = ({ user }) => {
  const axios = useAxiosPrivate();
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const { queryKey, queryFn } = leadEnquiriesQueries.leadEnquiries(
    user?.id,
    axios,
    { page, limit }
  );

  const { data, isLoading, error } = useQuery(queryKey, queryFn);

  const paginate = (pageNumber) => {
    setPage(pageNumber);
  };

  // If user is an agent, don't show this section
  if (user?.role_duplicate?.includes("agent")) {
    return null;
  }

  // Function to render key-value pairs from data object
  const renderDataFields = (dataObj) => {
    if (!dataObj) return null;

    // Format the key to be more readable
    const formatKey = (key) => {
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());
    };

    // Format the value based on its type
    const formatValue = (value) => {
      if (value === null || value === undefined) return 'N/A';

      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      if (typeof value === 'object') {
        try {
          return JSON.stringify(value, null, 2);
        } catch (e) {
          return String(value);
        }
      }

      return String(value);
    };

    return Object.entries(dataObj).map(([key, value]) => (
      <div className="data-item" key={key}>
        <div className="data-col">
          <div className="data-label">{formatKey(key)}</div>
          <div className="data-value">{formatValue(value)}</div>
        </div>
      </div>
    ));
  };

  if (error) {
    return (
      <Card className="card-bordered">
        <CardBody className="card-inner">
          <div className="text-center text-danger">
            Error loading lead enquiries: {error.message}
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="card-bordered">
      <CardBody className="card-inner">
        <div className="nk-block-head">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h5 className="nk-block-title">Lead Enquiries</h5>
              {data?.found > 0 && (
                <div className="nk-block-des text-soft">
                  <p>Total {data?.found} enquiries found</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center p-3">
            <Spinner color="primary" />
          </div>
        ) : data?.found === 0 ? (
          <div className="text-center text-muted p-3">No lead enquiries found</div>
        ) : (
          <>
            {data?.data.map((enquiry, index) => (
              <div key={enquiry._id || index} className="card card-bordered mb-3">
                <div className="card-inner">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">{enquiry.name}</h6>
                    <Badge color="light" className="text-dark">
                      {enquiry.source}
                    </Badge>
                  </div>

                  <ul className="d-flex flex-wrap g-2 pt-1">
                    <li className="mr-4">
                      <span className="text-muted fs-13px">Phone:</span>
                      <span className="fw-medium">{enquiry.phone}</span>
                    </li>
                    {enquiry.email && (
                      <li className="mr-4">
                        <span className="text-muted fs-13px">Email:</span>
                        <span className="fw-medium">{enquiry.email}</span>
                      </li>
                    )}
                    {enquiry.assignedAgent && (
                      <li className="mr-4">
                        <span className="text-muted fs-13px">Assigned Agent:</span>
                        <span className="fw-medium">
                          {typeof enquiry.agent === 'object'
                            ? `${enquiry.agent.user.first_name || ''} ${enquiry.agent.user.last_name || ''}`.trim()
                            : 'Agent ID: ' + enquiry.assignedAgent}
                        </span>
                      </li>
                    )}
                    <li>
                      <span className="text-muted fs-13px">Created:</span>
                      <span className="fw-medium">
                        {enquiry.createdAt
                          ? format(new Date(enquiry.createdAt), "dd MMM yyyy, h:mm a")
                          : "N/A"}
                      </span>
                    </li>
                  </ul>

                  {enquiry.data && Object.keys(enquiry.data).length > 0 && (
                    <div className="data-list is-compact mt-3">
                      <div className="data-head">
                        <h6 className="overline-title">Additional Data</h6>
                      </div>
                      {renderDataFields(enquiry.data)}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {data?.found > limit && (
              <div className="mt-3">
                <PaginationComponent
                  itemPerPage={limit}
                  totalItems={data?.found || 0}
                  paginate={paginate}
                  currentPage={page}
                />
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default LeadEnquiriesContainer;
